import { clamp } from "lodash-es";
import type {
  LocationQuery,
  NavigationGuardReturn,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
} from "vue-router";

import { internalActions } from "@/action/internalActions";
import { loadBoard, loadOtherBoards } from "@/backend/read/BackendReader";
import { sendBoardSwitch } from "@/composables/useEventBus";
import { handleError } from "@/error/errorHandler";
import { addBreadcrumb } from "@/error/sentry";
import { maxZoom, minZoom } from "@/model/Settings";
import type { BoardType } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import { isFlexBoard } from "@/model/board";
import type { Team } from "@/model/session";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useFlexStore } from "@/store/flex";
import { useGlobalErrorStore } from "@/store/globalError";
import { useLoadingStore } from "@/store/loading";
import { useNavigationStore } from "@/store/navigation";
import { useSessionStore } from "@/store/session";
import { NO_TEAM, useTeamStore } from "@/store/team";
import { useUserStore } from "@/store/user";
import { useZoomStore } from "@/store/zoom";
import { keysIn } from "@/utils/record";

import type { QueryImpl } from "../types";
import { ANY_TEAM, PARAM } from "../types";
import { initialTeamAndBoard } from "./appNavigation";
import type { ParamsAndQuery } from "./utils";
import { isBoardChange, replaceRoute } from "./utils";

let scrollTimeout = 0;

export async function handleNonSessionParams(
  to: RouteLocationNormalized,
  from: RouteLocationNormalizedLoaded,
): Promise<NavigationGuardReturn> {
  if (
    to.name !== "app" ||
    to.params.team === PARAM.NONE ||
    to.params.board === PARAM.NONE ||
    useGlobalErrorStore().globalError
  ) {
    return;
  }

  try {
    const pq = { params: {}, query: {} };
    if (to.params.board === "flex") {
      handleNameParam(pq, to.params.name as string | undefined);
    } else {
      await handleTeamAndBoardParams(
        pq,
        to.params.team as string,
        to.params.board as string,
        to.query,
      );
    }
    handleQuery(pq, from, to);
    return routeChange(pq, to);
  } catch (fail) {
    return handleError(fail);
  } finally {
    useSessionStore().setLoading(false);
  }
}

// load a flex board according to the "name" param
function handleNameParam(pq: ParamsAndQuery, toName: string | undefined) {
  const changeFlex =
    (toName && toName !== currentFlexBoardName()) || (!toName && !currentFlexBoardName());
  if (!changeFlex) {
    return;
  }
  const board = useFlexStore().flexBoardByName(toName || "") || useFlexStore().flexBoards[0];
  if (!board) {
    pq.params.board = "team";
    return;
  }
  useFlexStore().setCurrentFlexBoard(board);
  useBoardsStore().addBoard(board);

  const newBoard = internalActions.switchBoard(board);
  if (newBoard) {
    // no await: slow board loading should not block the navigation
    void loadBoard(board);
  }
  pq.params.name = board.name;
}

async function handleTeamAndBoardParams(
  pq: ParamsAndQuery,
  toTeam: string,
  toBoard: string,
  toQuery: LocationQuery,
) {
  let nextTeam = toTeam;
  let nextBoard = toBoard;

  if (toTeam === PARAM.INITIAL || toBoard === PARAM.INITIAL) {
    const { team, board } = await initialTeamAndBoard(useUserStore().technicalUser);
    nextTeam = team;
    nextBoard = board;
  } else if (toTeam === PARAM.LAST_TEAM) {
    const selectedTeam = useNavigationStore().lastSelectedTeamId(useSessionStore().current.id);
    if (selectedTeam) {
      nextTeam = selectedTeam;
    } else {
      const { team, board } = await initialTeamAndBoard(useUserStore().technicalUser);
      nextTeam = team;
      nextBoard = board;
    }
  } else if (toTeam === PARAM.CURRENT_TEAM) {
    nextTeam = useTeamStore().current.id === NO_TEAM.id ? ANY_TEAM : useTeamStore().current.id;
  }

  const team = findTeam(nextTeam);
  pq.params.team = team.name;

  const board = findBoard(nextBoard, team);
  pq.params.board = board.type;

  const newSession = useSessionStore().isSessionLoading;

  addBreadcrumb("navigation", {
    message: "Setting board/team",
    data: {
      switchedSession: newSession,
      teamId: team.id,
      teamName: team.name,
      oldTeamId: useTeamStore().current.id,
      board: board.type,
      oldBoard: useBoardStore().board?.type,
    },
  });

  let newBoard = false;
  if (team.id !== useTeamStore().current.id) {
    useTeamStore().setCurrent(team);
    newBoard = internalActions.switchBoard();
  }
  if (newSession || board.type !== useBoardStore().board?.type) {
    newBoard = internalActions.switchBoard(board);
  }

  applyZoomIfNeeded(pq, toQuery);
  if (newSession || newBoard) {
    const currentBoard = useBoardStore().currentBoard();
    const boardType = currentBoard.type;
    const teamName = boardType === "team" ? currentBoard.team?.name : undefined;

    addBreadcrumb("xhr", {
      message: "Reloading board",
      data: {
        id: useBoardStore().boardId(),
        name: currentFlexBoardName(),
        type: currentBoard.type,
        team: teamName,
      },
    });

    sendBoardSwitch();
    // no await: slow board loading should not block the navigation
    void loadBoard(currentBoard).then(loadOtherBoards);
    if (newSession) {
      useLoadingStore().end();
    }
  }
}

function currentFlexBoardName() {
  const currentBoard = useBoardStore().board;
  return isFlexBoard(currentBoard?.type) ? currentBoard.name : undefined;
}

function findTeam(target: string): Team {
  const match = useTeamStore().teams.find((team) => target === ANY_TEAM || matches(team));
  return match || useTeamStore().teams[0] || NO_TEAM;

  function matches(team: Team) {
    return team.name === target || team.id === target;
  }
}

function findBoard(target: string, team: Team): Board {
  return (
    useBoardsStore().boardByTypeOptional(target as BoardType, {
      teamId: team.id,
      artId: team.artId,
    }) ||
    useBoardsStore().boards[useBoardStore().board?.id || ""] ||
    useBoardsStore().boardByType("team", { teamId: team.id })
  );
}

function handleQuery(
  pq: ParamsAndQuery,
  from: RouteLocationNormalized,
  to: RouteLocationNormalized,
) {
  const newSession = useSessionStore().isSessionLoading;
  const stickyLink = isBoardChange(from, to) && to.query.zoom !== "1";
  scrollWindowIfNeeded(pq, to.query, newSession || stickyLink);
}

function applyZoomIfNeeded(pq: ParamsAndQuery, query: QueryImpl) {
  const newZoom = clamp(parseFloat(query.zoom!) || 1, minZoom, maxZoom);
  if (newZoom !== useZoomStore().factor) {
    useZoomStore().setFactors(newZoom);
    pq.query.zoom = "" + newZoom;
  }
}

function scrollWindowIfNeeded(pq: ParamsAndQuery, query: QueryImpl, force: boolean) {
  const scrollX = +(query.scrollX || 68);
  const scrollY = +(query.scrollY || 0);
  window.clearTimeout(scrollTimeout);
  // scrollX and scrollY are rounded, so we should check against rounded window.scrollX/Y
  // values to avoid unnecessary scrolls
  // (because scrollTo is in a timeout, this will sometimes trigger an undesired pan when zooming with
  // a mouse wheel with a browser-zoom level != 100% -- see https://rentouch.atlassian.net/browse/REN-14341)
  if (force || scrollX !== Math.round(window.scrollX) || scrollY !== Math.round(window.scrollY)) {
    const timeout = window.setTimeout(() => window.scrollTo(scrollX, scrollY), 50);
    // if it's an initial page load (=newSession) or a click on a sticky with a link to another page
    // scroll to the given position even if we get
    // another scroll position shortly afterwards
    scrollTimeout = force ? 0 : timeout;
    pq.query.scrollX = "" + scrollX;
    pq.query.scrollY = "" + scrollY;
  }
}

function routeChange(pq: ParamsAndQuery, to: RouteLocationNormalized): NavigationGuardReturn {
  const paramChanged = keysIn(pq.params).some((p) => pq.params[p] !== to.params[p]);
  const queryChanged = keysIn(pq.query).some((q) => pq.query[q] !== to.query[q]);

  if (paramChanged || queryChanged) {
    return replaceRoute(to, pq);
  }
}
